<template>
  <div class="fo-bars" @mouseleave="hover = null">
    <div class="fo-bars-plot" :style="{ height: `${height}px` }">
      <div class="fo-bars-axis">
        <span v-for="tick in scale.ticks" :key="tick.value"
              :style="{ bottom: `${tick.position}%` }">{{ tick.label }}</span>
      </div>
      <div class="fo-bars-area">
        <span class="fo-bars-grid" v-for="tick in scale.ticks" :key="tick.value"
              :class="{ 'is-base': tick.value === 0 }"
              :style="{ bottom: `${tick.position}%` }"></span>
        <div class="fo-bars-group" v-for="(group, index) in groups" :key="group.key"
             :class="{ 'is-hover': hover === index, 'is-partial': group.partial }"
             @mouseenter="hover = index">
          <div class="fo-bars-stack" :style="stackStyle">
            <span class="fo-bar fo-bar-revenue"
                  :style="{ height: `${heightOf(group.revenue)}%` }"></span>
            <span class="fo-bar fo-bar-expense"
                  :style="{ height: `${heightOf(group.expense)}%` }"></span>
          </div>
        </div>
        <template v-if="hasLine">
          <svg class="fo-bars-line" viewBox="0 0 100 100" preserveAspectRatio="none"
               aria-hidden="true">
            <polyline :points="linePoints" vector-effect="non-scaling-stroke"/>
          </svg>
          <span class="fo-bars-dot" v-for="(point, index) in points" :key="point.key"
                :class="{ 'is-hover': hover === index, 'is-partial': point.partial }"
                :style="{ left: `${point.x}%`, bottom: `${point.y}%` }"></span>
        </template>
        <div class="fo-bars-tip" v-if="hovered" :class="tipAlign" :style="tipStyle">
          <slot name="tip" :group="hovered"/>
        </div>
      </div>
    </div>
    <div class="fo-bars-labels">
      <span v-for="(group, index) in groups" :key="group.key"
            :class="{ 'is-hover': hover === index }">
        {{ group.label }}<small v-if="group.note">{{ group.note }}</small>
      </span>
    </div>
  </div>
</template>

<script>
import { compact, niceRange, ticks } from './helpers';

export default {
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    hidden: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 180,
    },
  },
  data() {
    return {
      hover: null,
    };
  },
  computed: {
    hasLine() {
      return this.groups.some(group => typeof group.line === 'number');
    },
    scale() {
      const values = this.groups.flatMap(group => [
        Number(group.revenue || 0),
        Number(group.expense || 0),
        ...(this.hasLine ? [Number(group.line || 0)] : []),
      ]);
      const range = niceRange(Math.min(0, ...values), Math.max(0, ...values));
      return {
        ...range,
        ticks: ticks(range).map(value => ({
          value,
          position: this.positionOf(value, range),
          label: value === 0 ? '0' : compact(value, this.hidden),
        })),
      };
    },
    zero() {
      return this.positionOf(0, this.scale);
    },
    stackStyle() {
      return { bottom: `${this.zero}%`, height: `${100 - this.zero}%` };
    },
    points() {
      const width = 100 / this.groups.length;
      return this.groups.map((group, index) => ({
        key: group.key,
        partial: group.partial,
        x: (index + 0.5) * width,
        y: this.positionOf(Number(group.line || 0), this.scale),
      }));
    },
    linePoints() {
      return this.points.map(point => `${point.x},${100 - point.y}`).join(' ');
    },
    hovered() {
      return this.hover === null ? null : this.groups[this.hover];
    },
    tipAlign() {
      if (this.hover < 2) {
        return 'is-start';
      }
      return this.hover > this.groups.length - 3 ? 'is-end' : 'is-center';
    },
    tipStyle() {
      const width = 100 / this.groups.length;
      if (this.tipAlign === 'is-start') {
        return { left: `${this.hover * width}%` };
      }
      if (this.tipAlign === 'is-end') {
        return { left: `${(this.hover + 1) * width}%` };
      }
      return { left: `${(this.hover + 0.5) * width}%` };
    },
  },
  methods: {
    positionOf(value, range) {
      return ((value - range.min) / (range.max - range.min)) * 100;
    },
    heightOf(value) {
      return this.scale.max > 0 ? (Math.max(0, Number(value || 0)) / this.scale.max) * 100 : 0;
    },
  },
};
</script>
